import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { API_CONFIG } from '../config/config';
import httpInterceptor from '../utils/httpInterceptor';

const AuthContext = createContext();

// Token validation cache
const tokenValidationCache = {
  token: null,
  isValid: false,
  timestamp: 0,
  // Cache duration in milliseconds (15 minutes)
  cacheDuration: 15 * 60 * 1000,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [logoutMessage, setLogoutMessage] = useState(null);
  const validationInProgress = useRef(false);

  const handleTokenInvalidation = async (reason = 'Token invalidated') => {
    console.log('Token invalidated, logging out user');
    setLogoutMessage(`Sie wurden abgemeldet: ${reason}. Weiterleitung zur Anmeldeseite...`);
    
    // Show message for 2 seconds before redirecting
    setTimeout(async () => {
      await logout(true); // Redirect to login
    }, 2000);
  };

  // Setup HTTP interceptor
  useEffect(() => {
    httpInterceptor.setAuthContext({
      handleTokenInvalidation: handleTokenInvalidation
    });
    
    // Start periodic token validation
    httpInterceptor.startPeriodicValidation(60000); // Check every minute
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  const validateToken = async (token) => {
            // console.log('Validating token:', token ? 'present' : 'missing');
    
    if (!token) {
      // console.log('No token provided for validation');
      setLoading(false);
      return false;
    }

    // Check if token is expired locally first
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;
      
      // If token expires within 5 minutes, try to refresh it
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        console.log('Token expires soon, attempting refresh');
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return true; // Token was refreshed successfully
        }
      }
      
      if (currentTime >= expirationTime) {
        console.log('Token is expired locally');
        // Try to refresh before giving up
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          setUser(null);
          clearTokenCache();
          setLoading(false);
          return false;
        }
        return true; // Token was refreshed successfully
      }
      
              // console.log('Token expiration check passed, expires at:', new Date(expirationTime));
    } catch (e) {
      console.log('Could not decode token for expiration check:', e);
      // Continue with server validation
    }
    
    // Check cache first
    if (tokenValidationCache.token === token) {
      const now = Date.now();
      if (now - tokenValidationCache.timestamp < tokenValidationCache.cacheDuration) {
        // console.log('Using cached token validation');
        setLoading(false);
        return tokenValidationCache.isValid;
      }
    }

    // Prevent multiple simultaneous validations
    if (validationInProgress.current) {
      // console.log('Validation already in progress');
      return false;
    }

    validationInProgress.current = true;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // If validation fails, try to refresh the token
        console.log('Token validation failed, attempting refresh');
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return true; // Token was refreshed successfully
        }
        throw new Error('Token validation failed');
      }

      const userData = await response.json();
      // console.log('Token validation successful, user data:', userData);
      setUser({
        ...userData,
        mfa_enabled: userData.mfa_enabled || false
      });
      
      // Update cache
      tokenValidationCache.token = token;
      tokenValidationCache.isValid = true;
      tokenValidationCache.timestamp = Date.now();
      
      return true;
    } catch (err) {
      console.error('Token validation error:', err);
      // Only clear tokens and user state if it's a clear authentication error
      // Don't clear on network errors or temporary issues
      if (err.message && (
        err.message.includes('401') || 
        err.message.includes('403') || 
        err.message.includes('Token validation failed')
      )) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        
        // Clear validation cache completely
        tokenValidationCache.token = null;
        tokenValidationCache.isValid = false;
        tokenValidationCache.timestamp = 0;
        
        // Handle automatic logout for token invalidation
        await handleTokenInvalidation();
      }
      
      return false;
    } finally {
      validationInProgress.current = false;
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    if (!refreshTokenValue) {
      console.log('No refresh token available');
      return false;
    }

    try {
      console.log('Attempting to refresh token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue,
        }),
      });

      if (!response.ok) {
        console.log('Token refresh failed');
        return false;
      }

      const data = await response.json();
      
      if (data.access_token) {
        console.log('Token refresh successful');
        localStorage.setItem('token', data.access_token);
        
        // Update refresh token if a new one is provided
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        
        // Clear cache to force revalidation with new token
        clearTokenCache();
        
        // Validate the new token
        const isValid = await validateToken(data.access_token);
        return isValid;
      }
      
      return false;
    } catch (err) {
      console.error('Token refresh error:', err);
      return false;
    }
  };

  // Validate token on mount and periodically
  useEffect(() => {
    // console.log('AuthProvider mounted, checking for token');
    const token = getToken();
    if (token) {
      // console.log('Token found, validating...');
      validateToken(token).catch(err => {
        console.error('Token validation failed on mount:', err);
        // Don't clear user state immediately on validation failure
        // Let the user try to use the app first
        setLoading(false);
      });
      
      // Set up periodic validation
      const interval = setInterval(() => {
        validateToken(token).catch(err => {
          console.error('Periodic token validation failed:', err);
        });
      }, tokenValidationCache.cacheDuration);
      
      return () => clearInterval(interval);
    } else {
      // console.log('No token found, setting loading to false');
      setLoading(false);
    }
  }, []);

  const login = async (email, password, csrfToken, mfaCode = null) => {
    try {
      setError(null);
      setLoading(true);
      console.log('Attempting login with:', { 
        email, 
        csrfToken, 
        hasMfaCode: !!mfaCode,
        passwordLength: password ? password.length : 0
      });
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/login/step2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          csrf_token: csrfToken,
          mfa_code: mfaCode,
        }),
      });

      console.log('Login response status:', response.status);
      const data = await response.json();
      console.log('Login response data:', data);
      
      // Check for rate limit error
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const waitTime = retryAfter ? parseInt(retryAfter) : 60;
        setError(`Too many requests. Please wait ${waitTime} seconds before trying again.`);
        return false;
      }
      
      // Check for MFA required first, regardless of response status
      if (data.status === 'mfa_required') {
        console.log('MFA required, setting error');
        setError('MFA-Code erforderlich');
        return false;
      }

      if (!response.ok) {
        console.log('Login failed with error:', data.detail);
        if (data.detail === 'Invalid MFA code') {
          setError('Invalid MFA code');
        } else {
          setError(data.detail || 'Login failed');
        }
        return false;
      }

      if (!data.access_token) {
        console.log('No access token in response');
        setError('No access token received from server');
        return false;
      }
      
      console.log('Login successful, storing tokens and user data');
      // Store the access token
      localStorage.setItem('token', data.access_token);
      
      // Store the refresh token
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      // Store user data
      if (data.user) {
        // console.log('Setting user data:', data.user);
        setUser({
          ...data.user,
          mfa_enabled: data.user.mfa_enabled || false
        });
      }
      
      // Validate the token immediately
      const isValid = await validateToken(data.access_token);
      // console.log('Token validation result:', isValid);
      
      if (!isValid) {
        setError('Token validation failed');
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (redirectToLogin = true) => {
    console.log('Logging out user');
    
    try {
      // Call backend logout endpoint
      const token = getToken();
      if (token) {
        await fetch(`${API_CONFIG.BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear local state regardless of API call result
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    
    // Clear all quiz-related localStorage items for security (prevent cross-user data leakage)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('quiz_result_') || key.startsWith('quiz_completed_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    setUser(null);
    // Clear validation cache
    clearTokenCache();
    
    // Redirect to login page if requested
    if (redirectToLogin) {
      window.location.href = '/login';
    }
  };

  const getToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found in localStorage');
      return null;
    }
    return token;
  };

  const updateUser = (userData) => {
    console.log('Updating user data:', userData);
    setUser(prevUser => ({
      ...prevUser,
      ...userData,
      mfa_enabled: userData.mfa_enabled || false
    }));
  };

  const clearTokenCache = () => {
    console.log('Clearing token validation cache');
    tokenValidationCache.token = null;
    tokenValidationCache.isValid = false;
    tokenValidationCache.timestamp = 0;
  };

  const forceRevalidate = async () => {
    console.log('Force revalidating token');
    clearTokenCache();
    const token = getToken();
    if (token) {
      return await validateToken(token);
    }
    return false;
  };

  const value = {
    user,
    error,
    loading,
    logoutMessage,
    login,
    logout,
    getToken,
    updateUser,
    clearTokenCache,
    forceRevalidate,
    handleTokenInvalidation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext; 